import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';
import { IdentityAvatar, DefinitionGrid, StatusBadge, designTokens } from '../../../design-system';
import type { SelfProfileWorker } from './model';
import type { ProfilePhotoPort } from './port';
import { ProfilePhotoControl } from './ProfilePhotoControl';
import { profileDateValue, profileTextValue } from './profileDisplay';
import { workerStatusPresentation } from './workerStatusPresentation';

type EmployeeProfileSummaryProps = {
  worker: SelfProfileWorker;
  port: ProfilePhotoPort;
  fixtureMode: boolean;
  readOnly: boolean;
  onPhotoChanged: () => void;
};

export function EmployeeProfileSummary({ worker, port, fixtureMode, readOnly, onPhotoChanged }: EmployeeProfileSummaryProps) {
  const name = worker.descriptor || '이름 미등록';
  const job = worker.primaryJob;
  const organization = profileTextValue(job?.supervisoryOrganization?.descriptor);
  const email = profileTextValue(worker.person?.email);

  return <Paper component="section" aria-label="내 프로필 요약" variant="outlined" sx={{
    p: { xs: 2, sm: 3 }, borderRadius: designTokens.radius.surface,
    bgcolor: designTokens.color.neutralRaisedSurface,
  }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.3fr) minmax(0, 1fr)' }, gap: 3, alignItems: 'center' }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0 }}>
        <Box sx={{ flexShrink: 0, width: 92 }}>
          {worker.person ? <ProfilePhotoControl
            port={port} personId={worker.person.id} name={name} photoUrl={worker.person.profilePhotoUrl}
            fixtureMode={fixtureMode} presentation="profileHeader" readOnly={readOnly} onChanged={onPhotoChanged}
          /> : <IdentityAvatar sx={{ width: 92, height: 92 }}>{Array.from(name)[0]}</IdentityAvatar>}
        </Box>
        <Stack spacing={0.75} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography component="h2" sx={{ ...designTokens.typography.sectionTitle, overflowWrap: 'anywhere' }}>{name}</Typography>
            <StatusBadge {...workerStatusPresentation(worker.status)} />
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{job?.businessTitle || job?.jobProfile?.descriptor || '직무 미등록'}</Typography>
          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr)', columnGap: 1, alignItems: 'baseline', minWidth: 0 }}>
            <Typography component="dt" variant="caption" color="text.secondary">소속 조직</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0, minWidth: 0, color: organization ? designTokens.color.primaryText : designTokens.color.secondaryText, overflowWrap: 'anywhere' }}>
              {organization || '미등록'}
            </Typography>
            {worker.additionalJobs?.map((additionalJob) => <Fragment key={additionalJob.id}>
              <Typography component="dt" variant="caption" color="text.secondary">겸직</Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
                {profileTextValue(additionalJob.supervisoryOrganization?.descriptor) || '조직 미등록'}
              </Typography>
            </Fragment>)}
          </Box>
        </Stack>
      </Stack>
      <Box sx={{ minWidth: 0, borderLeft: { md: `1px solid ${designTokens.color.divider}` }, borderTop: { xs: `1px solid ${designTokens.color.divider}`, md: 0 }, pl: { md: 3 }, pt: { xs: 2, md: 0 } }}>
        <DefinitionGrid items={[
          { id: 'number', label: '사번', value: profileTextValue(worker.workerId) },
          { id: 'hired', label: '최근 입사일', value: profileDateValue(worker.hireDate) },
        ]} />
        {email && <Link href={`mailto:${email}`} underline="hover" sx={{ display: 'inline-block', mt: 1.5, ...designTokens.typography.ui, overflowWrap: 'anywhere', maxWidth: '100%' }}>{email}</Link>}
      </Box>
    </Box>
  </Paper>;
}
